import { useCallback, useMemo, useState } from 'react';
import moment, { MomentInput } from 'moment';
import { Button } from '@mui/material';
import { useFormikContext } from 'formik';
import { FormPart } from '@modules/shared/components/FormPart';
import { requestCreateDealComment } from '@modules/ContactManager/components/Sidebar/CommentsTabs/queries';
import { COMMENT_TYPES } from '@modules/ContactManager/components/Sidebar/CommentsTabs/types';
import { statesList } from '@shared/constants/statesList';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { TextInputField } from '@shared/fields/TextInputField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { datediff, openPdf, parseDate } from '@shared/commonFunctions';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { dateFormat } from '@shared/constants/formats';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { CONFIRMATION_RESULT, Roles } from '@shared/constants/constants';
import { Modal } from '@shared/components/modals/Modal';
import { ECo, IDealSummary } from '../../Deals/interfaces';
import { requestNcicCode, requestNcicCodeByMake } from '../requests';
import { ADDFileType, IFormAsset, INcicCodeResponse } from '../interfaces';
import { IVehicleTitleInfoData, pullRegistrationInfo } from '../../../shared';
import { NcicCodeModal } from './NcicCodeModal';

const ASSET_REGISTRATION_PREFIX = 'assetRegistration';

export const RegistrationForm = ({ pickedDeal }: { pickedDeal?: IDealSummary }) => {
  const { setFieldValue, values } = useFormikContext<IFormAsset>();
  const { isAdmin, userHasRoles } = useUserAccount();

  const isPullRegEnable = isAdmin || userHasRoles([Roles.PULL_REGISTRATION]);
  const hasEditLicensePlateRole = isAdmin || userHasRoles([Roles.EDIT_PLATE_2_VIN]);

  const { userId } = useUserAccount();
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [openTitleModal, setOpenTitleModal] = useState(false);
  const [openChildSupportModal, setOpenChildSupportModal] = useState(false);
  const [openLpLeadOverrideModal, setOpenLpLeadOverrideModal] = useState(false);

  const [alertMessage, setAlertMessage] = useState('');
  const [titleMessage, setTitleMessage] = useState('');
  const [childSupportMessage, setChildSupportMessage] = useState('');
  const [ncicCodes, setNcicCodes] = useState<INcicCodeResponse[]>([]);
  const [isNcicCodeModalOpen, setNcicCodeModalOpen] = useState(0);

  const enablePlate2Vin = useMemo(() => (
    values.isPlateToVinSuccess ? hasEditLicensePlateRole : true
  ), [values.isPlateToVinSuccess, hasEditLicensePlateRole]);

  const hasOverrideRoles = useMemo(() => isAdmin || userHasRoles([Roles.LP_Lead, Roles.LP_Manager]), [isAdmin, userHasRoles]);

  const postComment = useCallback(
    async (comment: string) => {
      pickedDeal?.dealId &&
        (await requestCreateDealComment(pickedDeal?.dealId, {
          comment: comment,
          commentActionType: COMMENT_TYPES.Comment,
        }));
    },
    [pickedDeal]
  );

  const showAlertAndPostComment = useCallback(
    (message: string, comment: string) => {
      setOpenAlertModal(true);
      setAlertMessage(message);
      postComment(comment);
    },
    [pickedDeal]
  );

  const callPullRegistration = useCallback(
    async (ncicCode: string, overridePullConfirmation: CONFIRMATION_RESULT | CONFIRMATION_RESULT.PENDING) => {
      setOpenLpLeadOverrideModal(false);
      const { make, year, vin, id, assetRegistration } = values;

      if (overridePullConfirmation == CONFIRMATION_RESULT.CANCEL) {
        return;
      }

      // Todo - add same enum on API side too to make it easy for all type of conformation popups
      let reuseData = undefined;
      switch (overridePullConfirmation) {
        case CONFIRMATION_RESULT.YES: {
          reuseData = false;
          break;
        }
      }

      const postData: IVehicleTitleInfoData = {
        stateCode: assetRegistration?.state,
        userId: userId ?? '',
        vehicleServiceType: ADDFileType.Pdf,
        caseNumber: '',
        make: make,
        year: year?.toString() ?? '',
        requesterCode: '',
        dealCoValue: pickedDeal?.co,
        ncicCode: ncicCode,
        reusePulledDataConfirmed: reuseData,
      };
      const data = await pullRegistrationInfo(vin, id, pickedDeal?.dealId ?? '', postData);

      if (data && data?.dataPulledInLast30Days && overridePullConfirmation == CONFIRMATION_RESULT.PENDING) {
        setOpenLpLeadOverrideModal(true);
        return;
      } else if (data) {
        const { transactionId } = data;
        const { accessTokenDoesNotExists, authorizeUrl, isError, errorDescription, fileInBytes } = data;

        if (accessTokenDoesNotExists) {
          window.open(authorizeUrl, '_new');
        } else if (isError) {
          Notistack.generalError(errorDescription);
        } else {
          openPdf(fileInBytes);
          postData.vehicleServiceType = ADDFileType.Xml;

          const xmlData = await pullRegistrationInfo(vin, id, pickedDeal?.dealId ?? '', postData, transactionId);

          const diffDate = (dateObj: MomentInput) => {
            return datediff(
              parseDate(moment(pickedDeal?.contractDate).format(dateFormat)),
              parseDate(moment(dateObj).format(dateFormat))
            );
          };

          if (xmlData) {
            if (xmlData.accessTokenDoesNotExists) {
              window.open(authorizeUrl, '_blank');
            } else if (xmlData.isError) {
              Notistack.generalError(xmlData.errorDescription);
            } else {
              let message = '';
              let comment = '';
              let anyDiscrepancies = false;

              const lienHolderName = xmlData?.dmV123Response?.lienHolder?.name ?? '';

              const formatLienHolderName = lienHolderName                
                .trim()
                .toLowerCase()
                .split(' ')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

              let commentMessagePrefix = 'Registration Pulled:\n';
              if (formatLienHolderName) {
                commentMessagePrefix += `Lienholder: ${formatLienHolderName}\n`;
              }

              setFieldValue('assetRegistration.titleNumber', xmlData?.dmV123Response?.title);

              if (xmlData?.dmV123Response?.title === '') {
                message = 'No Title # Found';
                comment = `${commentMessagePrefix}Title Discrepancy - No Title # Found`;
                setOpenTitleModal(true);
                setTitleMessage(message);
                postComment(comment);
                anyDiscrepancies = true;
              }

              // CHILD_SUPPORT_LIEN_PER check
              if (xmlData?.dmV123Response?.lienHolder?.entityUniqueId === 'CHILD_SUPPORT_LIEN_PER') {
                message = 'Child support Lien on file';
                comment = `${commentMessagePrefix}Child support Lien on file`;
                setOpenChildSupportModal(true);
                setChildSupportMessage(message);
                postComment(comment);
                anyDiscrepancies = true;
              }

              switch (assetRegistration?.state) {
                case 'FL':
                  if (
                    xmlData?.dmV123Response?.registrationStop !== '' &&
                    (pickedDeal?.co === ECo.Add || pickedDeal?.co === ECo.Drop || pickedDeal?.co === ECo.AddAndDrop)
                  ) {
                    message = 'FL Toll Violation';
                    comment = `${commentMessagePrefix}Issue Found - FL Toll Violation`;
                    showAlertAndPostComment(message, comment);
                    anyDiscrepancies = true;
                  }
                  break;
                case 'CA':
                  if (Number.isNaN(diffDate(xmlData.dmV123Response.registrationExpDate)) == false) {
                    if (diffDate(xmlData?.dmV123Response?.registrationExpDate) < 75) {
                      message = 'CA Registration Expires in Less than 75 Days';
                      comment = `${commentMessagePrefix}Issue Found - CA Registration Expires in Less than 75 Days`;
                      showAlertAndPostComment(message, comment);
                      anyDiscrepancies = true;
                    }
                  }
                  break;
                case 'SC':
                  if (Number.isNaN(diffDate(xmlData.dmV123Response.registrationExpDate)) == false) {
                    if (diffDate(xmlData?.dmV123Response?.registrationExpDate) < 120) {
                      message = 'SC Registration Expires in Less than 120 Days';
                      comment = `${commentMessagePrefix}Issue Found - SC Registration Expires in Less than 120 Days`;
                      showAlertAndPostComment(message, comment);
                      anyDiscrepancies = true;
                    }
                  }
                  break;
                default:
                  break;
              }
              if (!anyDiscrepancies) {
                comment = `${commentMessagePrefix}No Discrepancies Found`;
                postComment(comment);
                Notistack.successMessage(comment);
              }
            }
          }
        }
      }
    },
    [values, pickedDeal]
  );

  const handleNcicCodeClose = useCallback(
    (value: string) => {
      setNcicCodeModalOpen(0);
      callPullRegistration(value, CONFIRMATION_RESULT.PENDING);
    },
    [callPullRegistration]
  );

  const pullRegistration = useCallback(
    async (overridePullConfirmation: CONFIRMATION_RESULT) => {
      const { make, vin, id, assetRegistration } = values;

      if (vin && id && assetRegistration?.state) {
        if (assetRegistration.state == 'MO') {
          const response = await requestNcicCodeByMake(make);
          if (!response) {
            const lstCodes = await requestNcicCode();
            setNcicCodes(lstCodes);
            setNcicCodeModalOpen(prevState => prevState + 1);
          } else {
            callPullRegistration(response.description, overridePullConfirmation);
          }
        } else {
          callPullRegistration('', overridePullConfirmation);
        }
      } else if (!id) {
        Notistack.generalError('Please select an asset!');
      } else if (!assetRegistration?.state && !vin) {
        Notistack.generalError('Vin and Registration state are required!');
      } else if (!vin) {
        Notistack.generalError('Vin is required!');
      } else if (!assetRegistration?.state) {
        Notistack.generalError('Please select Registration state!');
      }
    },
    [pickedDeal, postComment, showAlertAndPostComment, userId, values]
  );

  const formConfig = useMemo(
    () => [
      {
        width: '83%',
        name: `${ASSET_REGISTRATION_PREFIX}.primaryOwner`,
        label: 'Owner 1',
        component: TextInputField,
      },
      {
        width: '17%',
        label: ' ',
        name: `${ASSET_REGISTRATION_PREFIX}.isPrimaryChange`,
        tooltipMessage: 'checked box indicates name change',
        component: CheckboxField,
      },
      {
        width: '83%',
        name: `${ASSET_REGISTRATION_PREFIX}.secondaryOwner`,
        label: 'Owner 2',
        component: TextInputField,
      },
      {
        width: '17%',
        label: ' ',
        name: `${ASSET_REGISTRATION_PREFIX}.isSecondaryChange`,
        tooltipMessage: 'checked box indicates name change',
        component: CheckboxField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.number`,
        label: 'Registration #',
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.state`,
        label: 'State',
        options: statesList,
        component: SelectAutocompleteField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.expirationDate`,
        label: 'Exp Date',
        component: SelectDateField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.registeredOwners`,
        label: 'Registered Owners',
        component: NumberInputField,
      },
      {
        width: '100%',
        name: `${ASSET_REGISTRATION_PREFIX}.plateNumber`,
        label: 'License Plate #',
        disabled: !enablePlate2Vin,
        component: TextInputField,
        onChange: (ev: { target: { value: string; }; }) => setFieldValue(`${ASSET_REGISTRATION_PREFIX}.plateNumber`, ev.target.value?.toUpperCase()),
      },
      {
        width: '100%',
        style: {
          marginLeft: ' 100px',
        },
        component: () => (
          <Button
            variant="contained"
            color="info"
            size="medium"
            disabled={!isPullRegEnable}
            style={{ padding: '18px 15px', height: '35px' }}
            onClick={() => {
              pullRegistration(CONFIRMATION_RESULT.PENDING);
            }}
          >
            Pull Registration Information
          </Button>
        ),
      },
    ],
    [pickedDeal?.contractDate, postComment, showAlertAndPostComment, userId, values, enablePlate2Vin]
  );

  return (
    <>
      <FormPart
        name="Registration"
        config={formConfig}
        marginTop={1}
        marginBottom={2}
      />
      <AlertModal
        title={'TITLE DISCREPANCIES:'}
        subtitle={titleMessage}
        buttonText={'Ok'}
        open={openTitleModal}
        onConfirm={() => setOpenTitleModal(false)}
        onClose={() => setOpenTitleModal(false)}
      />
      <AlertModal
        title={'REGISTRATION ISSUE:'}
        subtitle={childSupportMessage}
        buttonText={'Ok'}
        open={openChildSupportModal}
        onConfirm={() => setOpenChildSupportModal(false)}
        onClose={() => setOpenChildSupportModal(false)}
      />
      <AlertModal
        title={'REGISTRATION ISSUE:'}
        subtitle={alertMessage}
        buttonText={'Ok'}
        open={openAlertModal}
        onConfirm={() => setOpenAlertModal(false)}
        onClose={() => setOpenAlertModal(false)}
      />
      {!!isNcicCodeModalOpen && (
        <NcicCodeModal
          ncicCodeModalPosition={isNcicCodeModalOpen}
          handleClose={handleNcicCodeClose}
          ncicCodesFieldName="postData.ncicCode"
          ncicCodes={ncicCodes}
        />
      )}
      {openLpLeadOverrideModal && (
        <Modal
          showFooter
          open
          buttonText="Override"
          cancelButtonText="Cancel"
          title="Requires LP Lead Override."
          dialogWidth="500px"
          hideSave={!hasOverrideRoles}
          onClose={() => pullRegistration(CONFIRMATION_RESULT.CANCEL)}
          onConfirm={() => pullRegistration(CONFIRMATION_RESULT.YES)}
        ></Modal>
      )}
    </>
  );
};
